/**
 * Purchase Planner Tab (Consumption & Food Cost → Purchase Planner)
 * ════════════════════════════════════════════════════════════════
 * Intelligent purchase-recommendation engine that reads:
 *   • current stock  ← latest closing_qty from Consumption Grid
 *   • average daily usage ← consumption_entries in a chosen base window
 *   • last purchase date/rate/vendor ← consumption_invoices
 * Produces a per-item recommended purchase quantity, colour-coded reason,
 * category totals, summary tiles, and a one-click Purchase Order.
 */
import { CategoryBlocks, ViewSwitch } from "@/components/foodcost/FoodCostUI";
import { PlannerItemCards } from "@/components/foodcost/ItemCards";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, ShoppingCart, Sparkles, Download } from "lucide-react";
import { toast } from "sonner";
import MgtPOReviewModal from "./MgtPOReviewModal";

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_TYPES = [
  { v: "next_day",   label: "Next Day"   },
  { v: "next_week",  label: "Next Week"  },
  { v: "next_month", label: "Next Month" },
  { v: "custom",     label: "Custom Date Range" },
];
const BASE_TYPES = [
  { v: "last_7_days",         label: "Last 7 Days" },
  { v: "last_15_days",        label: "Last 15 Days" },
  { v: "last_30_days",        label: "Last 30 Days" },
  { v: "previous_month",      label: "Previous Month" },
  { v: "same_month_last_year",label: "Same Month Last Year" },
  { v: "custom",              label: "Custom Date Range" },
];

const COLOR_CLS = {
  red:    "bg-rose-50 border-l-4 border-rose-500",
  orange: "bg-orange-50 border-l-4 border-orange-500",
  yellow: "bg-amber-50 border-l-4 border-amber-400",
  green:  "bg-emerald-50 border-l-4 border-emerald-500",
};
const COLOR_BADGE = {
  red:    <Badge className="bg-rose-100 text-rose-800 border-rose-300">Urgent</Badge>,
  orange: <Badge className="bg-orange-100 text-orange-800 border-orange-300">Within 3d</Badge>,
  yellow: <Badge className="bg-amber-100 text-amber-800 border-amber-300">Monitor</Badge>,
  green:  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">OK</Badge>,
};

export default function PurchasePlannerTab({ session, center, fmt }) {
  const [planType, setPlanType] = useState("next_week");
  const [planFrom, setPlanFrom] = useState("");
  const [planTo, setPlanTo] = useState("");
  const [baseType, setBaseType] = useState("last_30_days");
  const [baseFrom, setBaseFrom] = useState("");
  const [baseTo, setBaseTo] = useState("");
  const [safetyPct, setSafetyPct] = useState(15);
  const [growthPct, setGrowthPct] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});     // {item_code: bool}
  const [manualQty, setManualQty] = useState({});   // {item_code: number}
  const [manualReason, setManualReason] = useState({}); // {item_code: {kind, text}}
  const [filter, setFilter] = useState("all");      // all|red|amber|green|selected|manual
  const [grouping, setGrouping] = useState("vendor");
  const [generating, setGenerating] = useState(false);
  const [poGenerated, setPoGenerated] = useState(null);
  // iter257 — Vendor filter + item search + vendor list
  const [vendors, setVendors] = useState([]);
  const [vendorFilter, setVendorFilter] = useState("");   // "" = all
  const [searchText, setSearchText] = useState("");
  // iter258 — PO list + MGT review modal
  const [poList, setPoList] = useState([]);
  const [poListLoading, setPoListLoading] = useState(false);
  const [reviewPoId, setReviewPoId] = useState(null);
  const [showPoPanel, setShowPoPanel] = useState(false);

  // Roles — MGT = super_admin | admin | accounts | management | mgt
  const isMgt = useMemo(() => {
    const s = session || {};
    if (s.is_super_admin || s.is_admin || s.is_accounts) return true;
    const rk = String(s.role_key || "").toLowerCase();
    return ["super_admin","admin","accounts","management","mgt"].includes(rk);
  }, [session]);

  // Load vendors when centre changes
  useEffect(() => {
    let cancelled = false;
    if (!center) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/consumption/purchase-planner/vendors`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: session.token, center }),
        });
        const d = await r.json();
        if (!cancelled && r.ok) setVendors(d.vendors || []);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [center, session.token]);

  const load = useCallback(async () => {
    if (!center) return;
    setLoading(true);
    try {
      const body = {
        token: session.token, center,
        plan_type: planType,
        plan_from: planType === "custom" ? planFrom : undefined,
        plan_to: planType === "custom" ? planTo : undefined,
        base_type: baseType,
        base_from: baseType === "custom" ? baseFrom : undefined,
        base_to: baseType === "custom" ? baseTo : undefined,
        safety_pct: Number(safetyPct) || 0,
        growth_pct: Number(growthPct) || 0,
        vendor: vendorFilter || undefined,
        search: searchText.trim() || undefined,
      };
      const r = await fetch(`${API}/api/consumption/purchase-planner/preview`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed to load plan");
      setData(d);
      // Preselect all rows with required qty > 0
      const nextSel = {};
      (d.rows || []).forEach(r => {
        if (r.recommended_purchase_qty > 0) nextSel[r.item_code] = true;
      });
      setSelected(nextSel);
      setPoGenerated(null);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [session.token, center, planType, planFrom, planTo, baseType, baseFrom, baseTo, safetyPct, growthPct, vendorFilter, searchText]);
  useEffect(() => { load(); }, [load]);

  // iter258 — Load PO list for the centre
  const loadPos = useCallback(async () => {
    if (!center) return;
    setPoListLoading(true);
    try {
      const r = await fetch(`${API}/api/consumption/purchase-planner/pos`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center }),
      });
      const d = await r.json();
      if (r.ok) setPoList(d.pos || []);
    } catch (_) {} finally { setPoListLoading(false); }
  }, [session.token, center]);
  useEffect(() => { if (showPoPanel) loadPos(); }, [showPoPanel, loadPos]);

  const rows = data?.rows || [];
  const summary = data?.summary || {};
  const catTotals = data?.category_totals || [];
  const insights = data?.ai_insights || [];
  const sym = data?.currency?.symbol || "";

  const MANUAL_REASON_OPTIONS = [
    "Upcoming Demand",
    "Festival Requirement",
    "Catering / Bulk Order",
    "Safety Stock",
    "Supplier Holiday",
    "Price Advantage / Bulk Purchase",
    "Management Instruction",
    "Other",
  ];

  // iter214: return "final" order qty for a row — manual override wins
  const orderQtyOf = (r) => {
    const mq = Number(manualQty[r.item_code]);
    if (Number.isFinite(mq) && mq > 0) return mq;
    return r.recommended_purchase_qty || 0;
  };
  const isManual = (r) =>
    Number(manualQty[r.item_code]) > 0
    && (r.recommended_purchase_qty || 0) === 0;
  const isOverride = (r) =>
    Number(manualQty[r.item_code]) > 0
    && (r.recommended_purchase_qty || 0) > 0
    && Number(manualQty[r.item_code]) !== r.recommended_purchase_qty;

  const [ppCat, setPpCat] = useState("");
  const [ppView, setPpView] = useState("both");
  const [ppGridOpen, setPpGridOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const filteredRows = useMemo(() => {
    const out = (() => { switch (filter) {
      case "red":
      case "amber":
      case "green":
        return rows.filter(r => (r.color === filter)
          || (filter === "red" && (r.color === "red" || r.color === "orange")));
      case "manual":
        return rows.filter(r => isManual(r) || isOverride(r));
      case "selected":
        return rows.filter(r => selected[r.item_code]);
      case "all":
      default:
        return rows;
    } })();
    return ppCat ? out.filter((r) => (r.category || "Uncategorized") === ppCat) : out;
  }, [rows, filter, selected, manualQty, ppCat]);

  const toggleAll = (v) => {
    const next = {};
    filteredRows.forEach(r => { next[r.item_code] = v; });
    setSelected(prev => ({ ...prev, ...next }));
  };
  const selectedRows = useMemo(
    () => rows.filter(r => selected[r.item_code]
                              && (orderQtyOf(r) > 0)),
    [rows, selected, manualQty]
  );
  const selectedTotal = useMemo(
    () => selectedRows.reduce((a, r) =>
      a + (orderQtyOf(r) * (r.current_wap || r.last_purchase_rate || 0)), 0),
    [selectedRows, manualQty]
  );

  const generatePO = async () => {
    if (selectedRows.length === 0) { toast.error("Select at least one item to include in the PO."); return; }
    // iter214: manual items must carry a reason
    for (const r of selectedRows) {
      if (isManual(r) || isOverride(r)) {
        const rk = manualReason[r.item_code];
        const kind = rk?.kind;
        const text = (rk?.text || "").trim();
        if (!kind) {
          toast.error(`${r.item_name}: pick a manual purchase reason.`);
          return;
        }
        if (kind === "Other" && text.length < 3) {
          toast.error(`${r.item_name}: 'Other' needs a remark (min 3 chars).`);
          return;
        }
      }
    }
    setGenerating(true);
    try {
      const r = await fetch(`${API}/api/consumption/purchase-planner/generate-po`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: session.token, center,
          plan_from: data.plan_window.from, plan_to: data.plan_window.to,
          grouping,
          rows: selectedRows.map(r => {
            const manual = isManual(r) || isOverride(r);
            const rk = manualReason[r.item_code] || {};
            const reason_kind = rk.kind || "";
            const reason_text = (rk.text || "").trim();
            return {
              item_code: r.item_code, item_name: r.item_name,
              category: r.category, unit: r.unit,
              qty: orderQtyOf(r),
              recommended_qty: r.recommended_purchase_qty || 0,
              rate: r.current_wap || r.last_purchase_rate || 0,
              vendor: r.preferred_vendor,
              reason: manual
                ? `${reason_kind}${reason_text ? " — " + reason_text : ""}`
                : r.reason,
              purchase_source: manual ? "MANUAL_ADD" : "AUTO_RECOMMENDED",
              system_status: r.color,
              system_recommended_qty: r.recommended_purchase_qty || 0,
              manual_reason_kind: manual ? reason_kind : "",
              manual_reason_text: manual ? reason_text : "",
            };
          }),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed to generate PO");
      setPoGenerated(d.po);
      toast.success(`PO ${d.po.po_number} created`);
    } catch (e) { toast.error(e.message); } finally { setGenerating(false); }
  };

  const downloadPO = async () => {
    if (!poGenerated) return;
    try {
      const r = await fetch(`${API}/api/consumption/purchase-planner/export-po`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, po_id: poGenerated._id }),
      });
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `${poGenerated.po_number}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  // iter263 — PDF download (both roles).  Backend hides Rate + Amount
  // columns for non-MGT unless MGT toggles pricing visibility on.
  // Filename is honoured from the backend's Content-Disposition so
  // Center Managers get `REQ_...pdf` and MGT gets `PO_...pdf`.
  const [pdfBusy, setPdfBusy] = useState(null);
  const downloadPoPdf = async (poId, poNumber) => {
    setPdfBusy(poId);
    const toastId = toast.loading("Generating PDF...");
    try {
      const r = await fetch(`${API}/api/consumption/purchase-planner/po/pdf`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, po_id: poId }),
      });
      if (!r.ok) {
        // Surface the actual backend error instead of a generic string.
        let detail = `HTTP ${r.status}`;
        try {
          const j = await r.json();
          detail = j.detail || detail;
        } catch { /* non-JSON body — leave status code */ }
        throw new Error(`Unable to generate Purchase Order PDF — ${detail}`);
      }
      // iter287 — defensive: validate the response is actually a PDF
      // before triggering the browser download.  Prevents "download
      // appears to do nothing" when backend accidentally returns HTML
      // or JSON while still responding 200.
      const ctype = (r.headers.get("Content-Type") || "").toLowerCase();
      const blob = await r.blob();
      if (!ctype.includes("pdf") || blob.size < 100) {
        throw new Error(`Server returned non-PDF content (type=${ctype || "unknown"}, size=${blob.size})`);
      }
      const disp = r.headers.get("Content-Disposition") || "";
      const m = disp.match(/filename\s*=\s*"?([^";]+)"?/i);
      const fname = m ? m[1] : `${isMgt ? "PO" : "REQ"}_${poNumber || "po"}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      // Revoke after a tick so the download has started.
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(`PDF downloaded (${fname})`, { id: toastId });
    } catch (e) {
      toast.error(e.message || "PDF download failed", { id: toastId });
    } finally {
      setPdfBusy(null);
    }
  };
  const downloadPoExcel = async (poId, poNumber) => {
    try {
      const r = await fetch(`${API}/api/consumption/purchase-planner/export-po`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, po_id: poId }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Excel download failed");
      }
      const blob = await r.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `${poNumber || "PO"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-3" data-testid="purchase-planner-tab">
      {/* Filters */}
      <Card><CardContent className="p-3">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
          <div>
            <Label className="text-xs">Planning Type</Label>
            <select value={planType} onChange={e => setPlanType(e.target.value)}
                    className="h-8 border rounded px-2 text-xs w-full" data-testid="pp-plan-type">
              {PLAN_TYPES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </div>
          {planType === "custom" && (
            <>
              <div><Label className="text-xs">Plan From</Label>
                <Input type="date" value={planFrom} onChange={e => setPlanFrom(e.target.value)} className="h-8" data-testid="pp-plan-from" /></div>
              <div><Label className="text-xs">Plan To</Label>
                <Input type="date" value={planTo} onChange={e => setPlanTo(e.target.value)} className="h-8" data-testid="pp-plan-to" /></div>
            </>
          )}
          <div>
            <Label className="text-xs">Consumption Base</Label>
            <select value={baseType} onChange={e => setBaseType(e.target.value)}
                    className="h-8 border rounded px-2 text-xs w-full" data-testid="pp-base-type">
              {BASE_TYPES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </div>
          {baseType === "custom" && (
            <>
              <div><Label className="text-xs">Base From</Label>
                <Input type="date" value={baseFrom} onChange={e => setBaseFrom(e.target.value)} className="h-8" data-testid="pp-base-from" /></div>
              <div><Label className="text-xs">Base To</Label>
                <Input type="date" value={baseTo} onChange={e => setBaseTo(e.target.value)} className="h-8" data-testid="pp-base-to" /></div>
            </>
          )}
          <div>
            <Label className="text-xs">Safety %</Label>
            <Input type="number" step="1" value={safetyPct} onChange={e => setSafetyPct(e.target.value)}
                   className="h-8" data-testid="pp-safety" />
          </div>
          <div>
            <Label className="text-xs">Growth %</Label>
            <Input type="number" step="1" value={growthPct} onChange={e => setGrowthPct(e.target.value)}
                   className="h-8" data-testid="pp-growth" />
          </div>
          {/* iter257 — Vendor filter (works irrespective of category) */}
          <div>
            <Label className="text-xs">Vendor</Label>
            <select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)}
                    className="h-8 border rounded px-2 text-xs w-full" data-testid="pp-vendor-filter">
              <option value="">— All Vendors —</option>
              {vendors.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Search Item</Label>
            <Input type="text" placeholder="Item name / code"
                   value={searchText} onChange={e => setSearchText(e.target.value)}
                   className="h-8 text-xs" data-testid="pp-search-item" />
          </div>
          <Button size="sm" onClick={load} disabled={loading} data-testid="pp-refresh">
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
            Recompute
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowPoPanel(v => !v)}
                    data-testid="pp-toggle-po-panel">
            {showPoPanel ? "Hide" : "Show"} POs
          </Button>
        </div>
        {data && (
          <p className="text-[10px] text-slate-500 mt-2">
            Planning: <strong>{data.plan_window.from}</strong> → <strong>{data.plan_window.to}</strong> ({data.plan_window.days} days) ·
            Base: <strong>{data.base_window.from}</strong> → <strong>{data.base_window.to}</strong> ({data.base_window.days} days)
            {vendorFilter && <> · Vendor: <strong>{vendorFilter}</strong></>}
            {searchText && <> · Search: <strong>{searchText}</strong></>}
          </p>
        )}
      </CardContent></Card>

      {loading ? (
        <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : !data ? (
        <p className="text-sm text-slate-500 text-center py-6">Set filters above and click Recompute.</p>
      ) : (
        <>
          {/* Summary Tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="pp-tiles">
            <Tile label="Items Reviewed" value={summary.total_items_reviewed} />
            <Tile label="To Purchase" value={summary.items_to_purchase} accent="rose" />
            <Tile label="Purchase Value" value={`${sym}${(summary.total_purchase_value || 0).toLocaleString()}`} accent="rose" />
            <Tile label="Current Stock Value" value={`${sym}${(summary.current_stock_value || 0).toLocaleString()}`} />
            <Tile label="Expected Consumption Value" value={`${sym}${(summary.expected_consumption_value || 0).toLocaleString()}`} />
            <Tile label="Safety Stock Value" value={`${sym}${(summary.safety_stock_value || 0).toLocaleString()}`} />
            <Tile label="Projected Closing Value" value={`${sym}${(summary.projected_closing_stock_value || 0).toLocaleString()}`} />
          </div>

          {/* AI Insights — collapsible cards, full text kept */}
          {insights.length > 0 && (
            <div className="bi-panel" data-testid="pp-insights-panel">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 font-serif text-xl font-bold text-[#750817]"><Sparkles className="h-5 w-5" />AI Recommendations ({insights.length})</h3>
                <button type="button" className="bi-pill" onClick={() => setAiOpen(!aiOpen)} data-testid="pp-insights-toggle">{aiOpen ? "Hide details" : "View details"}</button>
              </div>
              <div className="mt-3 grid gap-2 md:grid-cols-2" data-testid="pp-insights">
                {(aiOpen ? insights : insights.slice(0, 4)).map((s, i) => (
                  <div key={i} className={`rounded-xl border-2 bg-white p-3 text-base ${aiOpen ? "" : "line-clamp-2"} ${/critical|urgent|stock.?out|short/i.test(s) ? "border-red-300" : "border-[#e2b85a]"}`}>{s}</div>))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <ViewSwitch view={ppView} setView={(v) => { setPpView(v); if (v === "grid") { setPpGridOpen(true); setPpCat(""); } }} testid="pp-view" />
            {ppView === "both" && <button type="button" className="bi-pill" onClick={() => setPpGridOpen(!ppGridOpen)} data-testid="pp-full-toggle">{ppGridOpen ? "Hide full grid" : "Show full grid"}</button>}
          </div>
          {ppView !== "grid" && (
            <CategoryBlocks rows={rows} value={ppCat} onChange={setPpCat} testid="pp-cat"
              sub={(c) => { const rs = c ? rows.filter((r) => (r.category || "Uncategorized") === c) : rows; const n = rs.filter((r) => (r.recommended_purchase_qty || 0) > 0).length;
                return `${n} to buy · ${sym}${Math.round(rs.reduce((a, r) => a + (Number(r.estimated_purchase_value) || 0), 0)).toLocaleString()}`; }} />)}

          {/* Category Totals */}
          {catTotals.length > 0 && (
            <Card><CardContent className="p-3">
              <p className="text-xs font-bold text-slate-700 mb-1">Category-wise Purchase Value</p>
              <div className="flex flex-wrap gap-2 text-xs" data-testid="pp-cat-totals">
                {catTotals.map(c => (
                  <div key={c.category} className="border rounded px-2 py-1 bg-slate-50">
                    <span className="text-slate-600">{c.category}:</span>{" "}
                    <span className="font-mono font-semibold">{sym}{c.total_value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}

          {/* iter214 Filters */}
          <div className="flex flex-wrap items-center gap-1 text-xs">
            <span className="text-slate-500 mr-1">Filter:</span>
            {[
              ["all", `All (${rows.length})`],
              ["red", `Recommended (${rows.filter(r => r.color === "red" || r.color === "orange").length})`],
              ["amber", `Monitor (${rows.filter(r => r.color === "yellow").length})`],
              ["green", `OK / Green (${rows.filter(r => r.color === "green").length})`],
              ["manual", `Manual Added (${rows.filter(r => isManual(r) || isOverride(r)).length})`],
              ["selected", `Selected for PO (${Object.values(selected).filter(Boolean).length})`],
            ].map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)}
                        className={`px-2 py-0.5 rounded-full border ${filter === k
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
                        data-testid={`pp-filter-${k}`}>
                {label}
              </button>
            ))}
          </div>

          {ppView !== "grid" && <PlannerItemCards rows={filteredRows} sym={sym} manualQty={manualQty} setManualQty={setManualQty} selected={selected} setSelected={setSelected} isManualRow={(r) => isManual(r) || isOverride(r)} manualReason={manualReason} setManualReason={setManualReason} reasonOptions={MANUAL_REASON_OPTIONS} />}
          {/* Grid */}
          <Card className={ppView === "cards" || (ppView === "both" && !ppGridOpen) ? "hidden" : ""} data-testid="pp-full-card"><CardContent className="p-0 overflow-x-auto">
            <Table className="text-xs" data-testid="pp-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <Checkbox
                      checked={filteredRows.length > 0 && filteredRows.every(r => selected[r.item_code])}
                      onCheckedChange={(v) => toggleAll(!!v)}
                      data-testid="pp-select-all"
                    />
                  </TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead className="text-right">Opening</TableHead>
                  <TableHead className="text-right">Current Stock</TableHead>
                  <TableHead className="text-right">Avg / Day</TableHead>
                  <TableHead className="text-right">Plan Days</TableHead>
                  <TableHead className="text-right">Expected</TableHead>
                  <TableHead className="text-right">Safety</TableHead>
                  <TableHead className="text-right">System Suggested</TableHead>
                  <TableHead className="text-right">Manual Qty</TableHead>
                  <TableHead>Manual Reason</TableHead>
                  <TableHead className="text-right">Current WAP</TableHead>
                  <TableHead className="text-right">Est. Value</TableHead>
                  <TableHead>Preferred Vendor</TableHead>
                  <TableHead>Last Purchase</TableHead>
                  <TableHead>System Reason</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow><TableCell colSpan={19} className="text-center text-slate-500 py-6">No items match this filter.</TableCell></TableRow>
                ) : filteredRows.map(r => {
                  const isGreen = r.color === "green";
                  const finalQty = orderQtyOf(r);
                  const estValue = finalQty * (r.current_wap || r.last_purchase_rate || 0);
                  const manual = isManual(r) || isOverride(r);
                  const rk = manualReason[r.item_code] || {};
                  return (
                  <TableRow key={r.item_code} className={COLOR_CLS[r.color] || ""} data-testid={`pp-row-${r.item_code}`}>
                    <TableCell>
                      <Checkbox
                        checked={!!selected[r.item_code]}
                        onCheckedChange={(v) => {
                          setSelected(s => ({ ...s, [r.item_code]: !!v }));
                          // Auto-open a manual qty input for GREEN when selected
                          if (v && isGreen && !manualQty[r.item_code]) {
                            setManualQty(m => ({ ...m, [r.item_code]: "" }));
                          }
                        }}
                        data-testid={`pp-sel-${r.item_code}`}
                      />
                    </TableCell>
                    <TableCell className="font-semibold">
                      {r.item_name}
                      <span className="text-slate-400 text-[10px] ml-1">({r.item_code})</span>
                      {manual && (
                        <Badge className="ml-1 bg-indigo-100 text-indigo-800 border-indigo-300 text-[10px]"
                                 data-testid={`pp-manual-badge-${r.item_code}`}>
                          MANUAL ADD
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{r.category}</TableCell>
                    <TableCell>{r.unit}</TableCell>
                    <TableCell className="text-right font-mono">{r.opening_stock}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{r.current_stock}</TableCell>
                    <TableCell className="text-right font-mono">{r.avg_daily_consumption}</TableCell>
                    <TableCell className="text-right font-mono">{r.planning_days}</TableCell>
                    <TableCell className="text-right font-mono">{r.expected_consumption}</TableCell>
                    <TableCell className="text-right font-mono">{r.safety_stock}</TableCell>
                    <TableCell className={`text-right font-mono font-bold ${r.recommended_purchase_qty > 0 ? "text-rose-800" : "text-slate-400"}`}>
                      {r.recommended_purchase_qty}
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number" step="0.01" min="0"
                        placeholder={r.recommended_purchase_qty > 0 ? "override" : "0"}
                        value={manualQty[r.item_code] ?? ""}
                        onChange={(e) => setManualQty(m => ({ ...m, [r.item_code]: e.target.value }))}
                        className="h-7 w-20 text-xs font-mono text-right"
                        data-testid={`pp-manual-qty-${r.item_code}`}
                      />
                    </TableCell>
                    <TableCell>
                      {manual ? (
                        <div className="flex flex-col gap-1">
                          <select
                            value={rk.kind || ""}
                            onChange={(e) => setManualReason(m => ({
                              ...m,
                              [r.item_code]: { ...(m[r.item_code] || {}), kind: e.target.value },
                            }))}
                            className="h-7 border rounded px-1 text-[11px] w-40"
                            data-testid={`pp-manual-reason-kind-${r.item_code}`}
                          >
                            <option value="">— pick reason —</option>
                            {MANUAL_REASON_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                          {rk.kind === "Other" && (
                            <Input
                              placeholder="Remark (min 3 chars)"
                              value={rk.text || ""}
                              onChange={(e) => setManualReason(m => ({
                                ...m,
                                [r.item_code]: { ...(m[r.item_code] || {}), text: e.target.value },
                              }))}
                              className="h-7 text-[11px] w-40"
                              data-testid={`pp-manual-reason-text-${r.item_code}`}
                            />
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-300 text-[11px]">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">{fmt(r.current_wap)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      {fmt(estValue)}
                    </TableCell>
                    <TableCell>{r.preferred_vendor}</TableCell>
                    <TableCell>
                      {r.last_purchase_date ? (
                        <div className="text-[10px]">
                          <div>{r.last_purchase_date}</div>
                          <div className="text-slate-500">{fmt(r.last_purchase_rate)}</div>
                        </div>
                      ) : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <span className="text-[11px] leading-tight" title={r.reason}>{r.reason}</span>
                    </TableCell>
                    <TableCell>{COLOR_BADGE[r.color] || <Badge>{r.status}</Badge>}</TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent></Card>

          {/* Generate PO */}
          <Card><CardContent className="p-3 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-xs">Group By</Label>
              <select value={grouping} onChange={e => setGrouping(e.target.value)}
                      className="h-8 border rounded px-2 text-xs" data-testid="pp-grouping">
                <option value="vendor">Vendor</option>
                <option value="category">Category</option>
                <option value="flat">Item (no grouping)</option>
              </select>
            </div>
            <div className="text-xs text-slate-600">
              Selected: <strong data-testid="pp-selected-count">{selectedRows.length}</strong> item(s) ·
              Estimated value: <strong className="font-mono">{sym}{selectedTotal.toLocaleString()}</strong>
            </div>
            <Button size="sm" onClick={generatePO} disabled={generating || selectedRows.length === 0}
                    className="bg-emerald-700 hover:bg-emerald-800"
                    data-testid="pp-generate-po">
              {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ShoppingCart className="w-3 h-3 mr-1" />}
              Generate Purchase Order
            </Button>
            {poGenerated && (
              <div className="ml-auto flex items-center gap-2 text-xs">
                <span className="text-emerald-800 font-semibold" data-testid="pp-po-number">
                  ✓ {poGenerated.po_number}{isMgt ? ` · ${sym}${poGenerated.total_amount.toLocaleString()}` : ""} · <span className="uppercase">{poGenerated.status}</span>
                </span>
                <Button size="sm" variant="outline"
                          onClick={() => downloadPoPdf(poGenerated._id, poGenerated.po_number)}
                          data-testid="pp-download-po-pdf">
                  <Download className="w-3 h-3 mr-1" /> PDF
                </Button>
                {isMgt && (
                  <Button size="sm" variant="outline" onClick={downloadPO} data-testid="pp-download-po">
                    <Download className="w-3 h-3 mr-1" /> Excel
                  </Button>
                )}
                {isMgt && (
                  <Button size="sm" variant="secondary"
                            onClick={() => setReviewPoId(poGenerated._id)}
                            data-testid="pp-mgt-review-new-po">
                    MGT Review →
                  </Button>
                )}
              </div>
            )}
          </CardContent></Card>

          {/* iter258 — PO List Panel */}
          {showPoPanel && (
            <Card data-testid="pp-po-panel">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-slate-700">Purchase Orders — {center}</p>
                  <Button size="sm" variant="outline" onClick={loadPos}
                            disabled={poListLoading} data-testid="pp-po-refresh">
                    {poListLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : "Refresh"}
                  </Button>
                </div>
                {poList.length === 0 ? (
                  <p className="text-xs text-slate-500 py-3">No POs yet for this centre.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table className="text-xs">
                      <TableHeader>
                        <TableRow>
                          <TableHead>PO #</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Lines</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Invoice</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {poList.map(p => (
                          <TableRow key={p._id} data-testid={`pp-po-row-${p._id}`}>
                            <TableCell className="font-semibold">{p.po_number}</TableCell>
                            <TableCell>{(p.created_at || "").slice(0, 10)}</TableCell>
                            <TableCell className="text-center">{p.line_count}</TableCell>
                            <TableCell className="text-right font-mono">
                              {isMgt
                                ? `${sym}${Number(p.total_amount || 0).toLocaleString()}`
                                : "—"}
                            </TableCell>
                            <TableCell>
                              <Badge className={
                                p.status === "approved" ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
                                p.status === "rejected" ? "bg-rose-100 text-rose-800 border-rose-300" :
                                p.status === "returned_for_correction" ? "bg-orange-100 text-orange-800 border-orange-300" :
                                p.status === "mgt_review" ? "bg-amber-100 text-amber-800 border-amber-300" :
                                p.status === "submitted" ? "bg-sky-100 text-sky-800 border-sky-300" :
                                "bg-slate-100 text-slate-700 border-slate-300"
                              }>{(p.status || "draft").toUpperCase().replace(/_/g," ")}</Badge>
                            </TableCell>
                            <TableCell>
                              {p.invoiced
                                ? <Badge className="bg-indigo-100 text-indigo-800 border-indigo-300"
                                            data-testid={`pp-po-invoiced-${p._id}`}>
                                    INVOICED · {p.invoice_number}
                                  </Badge>
                                : <span className="text-slate-400">—</span>}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" variant="outline"
                                          onClick={() => downloadPoPdf(p._id, p.po_number)}
                                          data-testid={`pp-po-pdf-${p._id}`}
                                          title="Download PDF">
                                  <Download className="w-3 h-3" />
                                </Button>
                                {isMgt && (
                                  <Button size="sm" variant="outline"
                                            onClick={() => downloadPoExcel(p._id, p.po_number)}
                                            data-testid={`pp-po-excel-${p._id}`}
                                            title="Download Excel">
                                    XLS
                                  </Button>
                                )}
                                <Button size="sm" variant="outline"
                                          onClick={() => setReviewPoId(p._id)}
                                          data-testid={`pp-po-open-${p._id}`}>
                                  {isMgt ? "MGT Review" : "Open"}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* iter258/259 — MGT PO Review + Franchise Invoice Modal */}
          {reviewPoId && (
            <MgtPOReviewModal
              session={session}
              poId={reviewPoId}
              isMgt={isMgt}
              onClose={() => { setReviewPoId(null); loadPos(); }}
            />
          )}
        </>
      )}
    </div>
  );
}

function Tile({ label, value, accent }) {
  const cls = accent === "rose" ? "border-rose-300" : "border-slate-200";
  return (
    <Card className={cls}><CardContent className="p-2">
      <p className="text-[10px] text-slate-500">{label}</p>
      <p className="text-sm font-bold text-slate-800">{value ?? "—"}</p>
    </CardContent></Card>
  );
}
